<template>
  <div id="selectModal" tabindex="-1" aria-hidden="true" class="modal-backdrop">
    <div class="modal-container">
      <!-- Modal header -->
      <div class="modal-header">
        <button
          @click="closeModal()"
          type="button"
          class="close-button"
          data-modal-toggle="select-modal"
        >
          <svg
            class="w-4 h-4"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
          <span class="sr-only">Close modal</span>
        </button>
      </div>
      <!-- Modal body -->
      <div class="modal-body">
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {{
            eventDetails
              ? "Interested in this property? Fill out this form, and one of our real estate agents will contact you within 24 hours."
              : "Ready to start your property journey? Fill out this form, and one of our real estate agents will contact you within 24 hours to discuss your needs and preferences."
          }}
        </p>
        <form @submit.prevent="submitLead" class="space-y-6">
          <!-- Name Input -->
          <div class="relative">
            <input
              v-model="name"
              type="text"
              id="modal_name"
              class="form-input peer"
              placeholder=" "
              required
            />
            <label for="modal_name" class="form-label">Full Name</label>
          </div>
          <!-- Email Input -->
          <div class="relative">
            <input
              v-model="email"
              type="email"
              id="modal_email"
              class="form-input peer"
              placeholder=" "
              autocomplete="email"
              required
            />
            <label for="modal_email" class="form-label">Email Address</label>
          </div>
          <!-- Contact Number Input -->
          <div class="relative">
            <input
              v-model="contactNumber"
              type="tel"
              id="modal_contact_number"
              class="form-input peer"
              placeholder=" "
              required
            />
            <label for="modal_contact_number" class="form-label"
              >Mobile Number (e.g., 0712345678)</label
            >
          </div>
          <!-- Country Select -->
          <div class="relative">
            <label
              for="country"
              class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300"
              >Country</label
            >
            <v-select
              id="country"
              class="vs-custom"
              :options="countries"
              v-model="selectedCountry"
              label="name"
              :searchable="true"
              :clearable="false"
              placeholder="Select your country"
              :append-to-body="true"
              :calculate-position="calculateDropdownPosition"
            ></v-select>
          </div>
          <!-- Submit Button -->
          <button type="submit" class="submit-button group">
            <span>Request Property Information</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5 ml-2 transition-transform duration-200 ease-in-out group-hover:translate-x-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { ref, onMounted } from "vue";
import vSelect from "vue3-select";
import "vue3-select/dist/vue3-select.css";

import { useToast } from "vue-toastification";

export default {
  props: ["eventDetails"],
  data() {
    return {
      name: null,
      email: null,
      contactNumber: null,
    };
  },
  components: {
    vSelect,
  },
  setup() {
    const mobileNumber = ref("");
    const selectedCountry = ref("Kenya");
    const countries = ref([]);
    const toast = useToast();

    const fetchCountries = async () => {
      const primaryUrl = "https://restcountries.com/v3.1/all";
      const secondaryUrl =
        "https://restcountries.com/v2/all?fields=name,alpha2Code,callingCodes";

      const normalizeV3 = (data) =>
        data
          .filter(
            (c) =>
              c?.name?.common &&
              c?.cca2 &&
              c?.idd?.root &&
              Array.isArray(c.idd.suffixes) &&
              c.idd.suffixes.length
          )
          .map((c) => ({
            name: c.name.common,
            code: c.cca2,
            mobileNumberCode: c.idd.root + c.idd.suffixes[0],
          }));

      const normalizeV2 = (data) =>
        data
          .filter(
            (c) =>
              c?.name &&
              c?.alpha2Code &&
              Array.isArray(c.callingCodes) &&
              c.callingCodes.length
          )
          .map((c) => ({
            name: c.name,
            code: c.alpha2Code,
            mobileNumberCode: "+" + c.callingCodes[0],
          }));

      try {
        const res = await fetch(primaryUrl);
        if (!res.ok) throw new Error("Primary countries API failed");
        let data = await res.json();
        if (!Array.isArray(data)) data = Object.values(data);
        countries.value = normalizeV3(data);
      } catch (errPrimary) {
        console.warn("Primary country API failed, trying fallback", errPrimary);
        try {
          const res2 = await fetch(secondaryUrl);
          if (!res2.ok) throw new Error("Fallback API failed");
          const data2 = await res2.json();
          countries.value = normalizeV2(data2);
        } catch (errSecondary) {
          console.error("Both country API calls failed", errSecondary);
          // Minimal fallback list
          countries.value = [
            { name: "Kenya", code: "KE", mobileNumberCode: "+254" },
            { name: "Uganda", code: "UG", mobileNumberCode: "+256" },
            { name: "Tanzania", code: "TZ", mobileNumberCode: "+255" },
          ];
        }
      }
    };

    onMounted(() => {
      fetchCountries();
    });

    const formatContactNumber = (number, country) => {
      const countryData = countries.value.find((c) => c.name === country);
      if (countryData) {
        const mobileNumberCode = countryData.mobileNumberCode;
        // Remove any leading zero and prepend the mobileNumberCode
        return mobileNumberCode + number.replace(/^0+/, "");
      }
      return number;
    };

    const submitForm = () => {
      // Handle form submission
    };

    const calculateDropdownPosition = (dropdownList, component, { width }) => {
      dropdownList.style.width = width;

      const rect = component.$el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 150; // max-height we set

      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
        // Position below
        dropdownList.style.top = rect.bottom + "px";
        dropdownList.style.maxHeight =
          Math.min(dropdownHeight, spaceBelow - 10) + "px";
      } else {
        // Position above
        const maxHeightAbove = Math.min(dropdownHeight, spaceAbove - 10);
        dropdownList.style.top = rect.top - maxHeightAbove + "px";
        dropdownList.style.maxHeight = maxHeightAbove + "px";
      }

      dropdownList.style.left = rect.left + "px";
      dropdownList.style.position = "fixed";
      dropdownList.style.zIndex = "10000";

      // Apply dark mode styling directly if dark mode is active
      const isDarkMode =
        document.documentElement.classList.contains("dark") ||
        document.body.classList.contains("dark");

      if (isDarkMode) {
        dropdownList.style.background = "rgba(54, 69, 79, 0.98)";
        dropdownList.style.backdropFilter = "blur(20px)";
        dropdownList.style.webkitBackdropFilter = "blur(20px)";
        dropdownList.style.border = "1.5px solid rgba(212, 175, 55, 0.3)";
        dropdownList.style.borderRadius = "0.75rem";
        dropdownList.style.boxShadow =
          "0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)";
        dropdownList.style.color = "#f9fafb";

        // Apply dark styling to options
        setTimeout(() => {
          const options = dropdownList.querySelectorAll(".vs__dropdown-option");
          options.forEach((option) => {
            option.style.color = "#f9fafb";
            option.style.background = "transparent";
          });
        }, 0);
      }
    };

    return {
      toast,
      mobileNumber,
      selectedCountry,
      countries,
      formatContactNumber,
      submitForm,
      calculateDropdownPosition,
    };
  },

  methods: {
    async submitLead() {
      var url = process.env.VUE_APP_IP_ADDRESS + "/api/v1/lead";
      var formattedContactNumber = this.formatContactNumber(
        this.contactNumber,
        this.selectedCountry.name
          ? this.selectedCountry.name
          : this.selectedCountry
      );

      var payload = JSON.stringify({
        name: this.name,
        emailAddress: this.email,
        contactNumber: formattedContactNumber,
        country: this.selectedCountry.name
          ? this.selectedCountry.name
          : this.selectedCountry,
        listingId: this.eventDetails ? this.eventDetails?.id : null,
      });
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      axios
        .post(url, payload, config)
        .then((response) => {
          var res = response.data;
          this.response = res;

          if (response.status == "201") {
            this.listings = res.body.content;

            this.formSubmmitted = true;
            this.name = null;
            this.contactNumber = null;
            this.emailAddress = null;
          }

          this.toast.success("Your request has been submitted successfully");
          this.closeModal();
          return res;
        })
        .catch((error) => {
          console.log(error);
          if (error) {
            this.errorMessage = error.response.data.body.debugMessage;
            this.response = error.response.data;

            this.toast.error(this.errorMessage);
            this.formSubmitted = false;
          }
        });
    },

    closeModal() {
      this.$emit("closeModal", true);
    },
  },
};
</script>

<style scoped>
/* CSS Variables for consistency */
:root {
  --color-charcoal: #36454f;
  --color-gold: #d4af37;
  --color-bronze: #cd7f32;
  --color-card-light: rgba(255, 255, 255, 0.98);
  --color-card-dark: rgba(54, 69, 79, 0.98);
  --color-border-light: rgba(255, 255, 255, 0.4);
  --color-border-dark: rgba(255, 255, 255, 0.12);
}

/* Modal Backdrop */
.modal-backdrop {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.7) 0%,
    rgba(0, 0, 0, 0.5) 50%,
    rgba(0, 0, 0, 0.8) 100%
  );
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  padding: 1rem;
  backdrop-filter: blur(12px) saturate(1.3);
  -webkit-backdrop-filter: blur(12px) saturate(1.3);
  overflow-y: auto;
}

/* Modal Container */
.modal-container {
  border-radius: 1.5rem;
  width: 100%;
  max-width: 520px;
  position: relative;
  margin: auto;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  animation: modalFadeInScale 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  overflow: hidden;
}

/* Light mode styling - default */
.modal-container:not(.dark-mode) {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) saturate(1.8);
  -webkit-backdrop-filter: blur(20px) saturate(1.8);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15), 0 10px 25px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8) !important;
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
}

/* Explicit light mode when not in dark class */
html:not(.dark) .modal-container {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) saturate(1.8);
  -webkit-backdrop-filter: blur(20px) saturate(1.8);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15), 0 10px 25px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8) !important;
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
}

/* Dark mode styling */
.dark .modal-container,
html.dark .modal-container {
  background: var(--color-card-dark) !important;
  border: 1px solid var(--color-border-dark) !important;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4), 0 15px 35px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
}

/* Modal Fade-in Animation */
@keyframes modalFadeInScale {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(30px);
  }
  60% {
    opacity: 0.8;
    transform: scale(1.02) translateY(-5px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Modal Header */
.modal-header {
  padding: 1rem 1.5rem 0.75rem;
  background: linear-gradient(
    135deg,
    rgba(212, 175, 55, 0.08) 0%,
    rgba(212, 175, 55, 0.04) 50%,
    rgba(212, 175, 55, 0.02) 100%
  );
  border-bottom: 1px solid rgba(212, 175, 55, 0.15);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
}

.modal-header::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 2rem;
  right: 2rem;
  height: 2px;
  background: linear-gradient(
    90deg,
    var(--color-gold),
    var(--color-bronze),
    transparent
  );
  opacity: 0.3;
}

.modal-header h3 {
  color: var(--color-charcoal);
  font-weight: 700;
  font-size: 1.375rem;
  letter-spacing: -0.025em;
}

.dark .modal-header {
  background: linear-gradient(
    135deg,
    rgba(212, 175, 55, 0.12) 0%,
    rgba(212, 175, 55, 0.05) 50%,
    transparent 100%
  );
  border-bottom-color: var(--color-border-dark);
}

.dark .modal-header h3 {
  color: #f9fafb;
}

/* Modal Body */
.modal-body {
  padding: 2rem;
  overflow: visible;
  flex-grow: 1;
}

.modal-body p {
  color: var(--color-charcoal);
  opacity: 0.8;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.dark .modal-body p {
  color: #d1d5db;
}

/* Close Button */
.close-button {
  color: var(--color-charcoal);
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.8),
    rgba(255, 255, 255, 0.6)
  );
  border: 1px solid var(--color-border-light);
  padding: 0.75rem;
  border-radius: 50%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.close-button:hover {
  background: linear-gradient(135deg, var(--color-gold), var(--color-bronze));
  color: #ffffff;
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(212, 175, 55, 0.3);
}

.dark .close-button {
  color: #d1d5db;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1),
    rgba(255, 255, 255, 0.05)
  );
  border-color: var(--color-border-dark);
}

.dark .close-button:hover {
  background: linear-gradient(135deg, var(--color-gold), var(--color-bronze));
  color: #ffffff;
}

/* Form Input Base Styles */
.form-input {
  display: block;
  width: 100%;
  padding: 1rem 1.25rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--color-charcoal);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1.5px solid var(--color-border-light);
  border-radius: 0.75rem;
  appearance: none;
  outline: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.dark .form-input {
  color: #f9fafb;
  background: rgba(255, 255, 255, 0.05);
  border-color: var(--color-border-dark);
}

/* Input Focus Styles */
.form-input:focus {
  border-color: var(--color-gold);
  box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.2),
    0 4px 12px rgba(212, 175, 55, 0.15);
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.95);
}

.dark .form-input:focus {
  border-color: var(--color-gold);
  box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.3),
    0 4px 12px rgba(212, 175, 55, 0.2);
  background: rgba(255, 255, 255, 0.08);
}

/* Form Label Styles (Floating) */
.form-label {
  position: absolute;
  top: 1rem;
  left: 1.25rem;
  font-size: 0.9rem;
  color: var(--color-charcoal);
  opacity: 0.7;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(255, 255, 255, 0.9) 100%
  );
  padding: 0 0.5rem;
  transform-origin: 0 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  font-weight: 500;
}

.dark .form-label {
  color: #d1d5db;
  background: linear-gradient(
    to right,
    rgba(54, 69, 79, 0.9) 0%,
    rgba(54, 69, 79, 0.9) 100%
  );
}

/* Label transformation on focus or when input has value */
.form-input:focus ~ .form-label,
.form-input:not(:placeholder-shown) ~ .form-label {
  transform: translateY(-1.75rem) scale(0.85);
  color: var(--color-gold);
  opacity: 1;
  font-weight: 600;
}

.dark .form-input:focus ~ .form-label,
.dark .form-input:not(:placeholder-shown) ~ .form-label {
  color: var(--color-gold);
}

/* Submit Button Styles */
.submit-button {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #ffffff;
  background-color: #ca8a04;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.submit-button:hover {
  background-color: #a16207;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.submit-button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(202, 138, 4, 0.4);
}

.dark .submit-button {
  background-color: #d97706;
}

.dark .submit-button:hover {
  background-color: #b45309;
}

.dark .submit-button:focus {
  box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.5);
}

/* Custom v-select styles to match form inputs exactly */
.vs-custom {
  --vs-controls-color: rgba(156, 163, 175, 0.4);
  --vs-border-color: rgba(156, 163, 175, 0.4);
  --vs-border-radius: 0.75rem;
  --vs-line-height: 1.5;
  --vs-font-size: 0.95rem;

  --vs-dropdown-bg: rgba(255, 255, 255, 0.98);
  --vs-dropdown-color: var(--color-charcoal);
  --vs-dropdown-option-color: var(--color-charcoal);
  --vs-dropdown-option--active-bg: rgba(212, 175, 55, 0.1);
  --vs-dropdown-option--active-color: var(--color-gold);

  --vs-selected-bg: transparent;
  --vs-selected-color: var(--color-charcoal);

  --vs-search-input-color: var(--color-charcoal);
  --vs-controls-size: 1;
  --vs-transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Match form input styling exactly */
.vs-custom :deep(.vs__dropdown-toggle) {
  padding: 1rem 1.25rem;
  border: 1.5px solid rgba(156, 163, 175, 0.4);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 0.75rem;
  min-height: 3.5rem;
  font-size: 0.95rem;
  color: var(--color-charcoal);
}

.vs-custom :deep(.vs__selected-options) {
  padding: 0;
  flex-wrap: nowrap;
}

.vs-custom :deep(.vs__selected) {
  margin: 0;
  padding: 0;
  line-height: 1.5;
  font-size: 0.95rem;
  color: var(--color-charcoal);
  background: transparent;
  border: none;
}

.vs-custom :deep(.vs__search) {
  padding: 0;
  margin: 0;
  line-height: 1.5;
  font-size: 0.95rem;
  color: var(--color-charcoal);
}

.vs-custom :deep(.vs__search::placeholder) {
  color: rgba(107, 114, 128, 0.7);
}

/* Focus state to match form inputs */
.vs-custom:focus-within :deep(.vs__dropdown-toggle) {
  border-color: var(--color-gold);
  box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.2),
    0 4px 12px rgba(212, 175, 55, 0.15);
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.95);
}

/* Dropdown menu positioning and styling */
.vs-custom :deep(.vs__dropdown-menu) {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1.5px solid rgba(212, 175, 55, 0.2);
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05);
  max-height: 150px;
  overflow-y: auto;
  z-index: 10000;
}

.vs-custom :deep(.vs__dropdown-option) {
  padding: 0.75rem 1.25rem;
  font-size: 0.95rem;
  color: var(--color-charcoal);
  transition: all 0.2s ease;
}

.vs-custom :deep(.vs__dropdown-option--highlight) {
  background: rgba(212, 175, 55, 0.1);
  color: var(--color-gold);
}

/* Clear button styling */
.vs-custom :deep(.vs__clear) {
  margin-right: 0.5rem;
}

/* Open indicator styling */
.vs-custom :deep(.vs__open-indicator) {
  margin-right: 0.25rem;
}

/* Dark mode v-select overrides - Complete override approach */
.dark .vs-custom {
  --vs-controls-color: rgba(255, 255, 255, 0.12);
  --vs-border-color: rgba(255, 255, 255, 0.12);
  --vs-dropdown-bg: rgba(54, 69, 79, 0.98);
  --vs-dropdown-color: #f9fafb;
  --vs-dropdown-option-color: #f9fafb;
  --vs-dropdown-option--active-bg: rgba(212, 175, 55, 0.15);
  --vs-dropdown-option--active-color: var(--color-gold);
  --vs-selected-bg: transparent;
  --vs-selected-color: #f9fafb;
  --vs-search-input-color: #f9fafb;
}

/* Complete override to match form-input exactly in dark mode */
.dark .vs-custom :deep(.vs__dropdown-toggle) {
  background-color: rgba(255, 255, 255, 0.05) !important;
  background-image: none !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  border: 1.5px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 0.75rem !important;
  color: #f9fafb !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
}

/* Override any nested background styles */
.dark .vs-custom :deep(.vs__dropdown-toggle),
.dark .vs-custom :deep(.vs__dropdown-toggle) * {
  background: rgba(255, 255, 255, 0.05) !important;
}

.dark .vs-custom :deep(.vs__selected) {
  color: #f9fafb !important;
  background: transparent !important;
}

.dark .vs-custom :deep(.vs__search) {
  color: #f9fafb !important;
  background: transparent !important;
}

.dark .vs-custom :deep(.vs__search::placeholder) {
  color: rgba(209, 213, 219, 0.7) !important;
}

.dark .vs-custom:focus-within :deep(.vs__dropdown-toggle) {
  border-color: var(--color-gold) !important;
  box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.3),
    0 4px 12px rgba(212, 175, 55, 0.2) !important;
  background: rgba(255, 255, 255, 0.08) !important;
}

.dark .vs-custom :deep(.vs__dropdown-menu) {
  background: rgba(54, 69, 79, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1.5px solid rgba(212, 175, 55, 0.3) !important;
  border-radius: 0.75rem !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  z-index: 10000 !important;
  max-height: 150px !important;
}

.dark .vs-custom :deep(.vs__dropdown-option) {
  color: #f9fafb !important;
}

.dark .vs-custom :deep(.vs__dropdown-option--highlight) {
  background: rgba(212, 175, 55, 0.15) !important;
  color: var(--color-gold) !important;
}

/* Additional override for any remaining background issues */
.dark .vs-custom :deep(.vs__actions) {
  background: transparent !important;
}

.dark .vs-custom :deep(.vs__clear),
.dark .vs-custom :deep(.vs__open-indicator) {
  background: transparent !important;
}

/* Nuclear option - force exact match with form inputs */
.dark .modal-container .vs-custom .vs__dropdown-toggle {
  background: rgba(255, 255, 255, 0.05) !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  border: 1.5px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 0.75rem !important;
  color: #f9fafb !important;
  appearance: none !important;
  outline: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
}

/* Ensure all child elements inherit the background */
.dark .modal-container .vs-custom .vs__dropdown-toggle * {
  background: inherit !important;
}

/* Dark mode dropdown when appended to body */
.dark .vs__dropdown-menu {
  background: rgba(54, 69, 79, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1.5px solid rgba(212, 175, 55, 0.3) !important;
  border-radius: 0.75rem !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  color: #f9fafb !important;
}

.dark .vs__dropdown-option {
  color: #f9fafb !important;
  background: transparent !important;
}

.dark .vs__dropdown-option--highlight {
  background: rgba(212, 175, 55, 0.15) !important;
  color: var(--color-gold) !important;
}

/* Additional fallback for body-appended dropdown */
html.dark .vs__dropdown-menu {
  background: rgba(54, 69, 79, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1.5px solid rgba(212, 175, 55, 0.3) !important;
  border-radius: 0.75rem !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  color: #f9fafb !important;
}

html.dark .vs__dropdown-option {
  color: #f9fafb !important;
  background: transparent !important;
}

html.dark .vs__dropdown-option--highlight {
  background: rgba(212, 175, 55, 0.15) !important;
  color: var(--color-gold) !important;
}

/* Global dark mode styles for Vue Select (highest priority) */
body.dark .vs__dropdown-menu,
.dark .vs__dropdown-menu,
html.dark .vs__dropdown-menu {
  background: rgba(54, 69, 79, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1.5px solid rgba(212, 175, 55, 0.3) !important;
  border-radius: 0.75rem !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  color: #f9fafb !important;
}

body.dark .vs__dropdown-option,
.dark .vs__dropdown-option,
html.dark .vs__dropdown-option {
  color: #f9fafb !important;
  background: transparent !important;
}

body.dark .vs__dropdown-option--highlight,
.dark .vs__dropdown-option--highlight,
html.dark .vs__dropdown-option--highlight {
  background: rgba(212, 175, 55, 0.15) !important;
  color: #d4af37 !important;
}
</style>
